import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, ListItem, Typography } from '@mui/material';
import dayjs from 'dayjs';
import numeral from 'numeral';
import { useForecastHours } from '../../hooks/useForecasts';
import { WeatherIcon } from '../WeatherIcon';

type Props = {
    date: string;
    id: string;
    filterBy: number;
};

export const Hours = ({ date, id, filterBy }: Props) => {
    const { data: forecastHours = [] } = useForecastHours(id, date);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', pb: 2 }}>
            <Box sx={{ display: 'flex', p: 2 }}>
                <Typography variant="body1" sx={{ width: 80 }}>
                    {`Hour`}
                </Typography>
                <Box sx={{ width: 40 }} />
                <Typography variant="body1" sx={{ width: 80 }}>
                    {`Temp`}
                </Typography>
                <Typography variant="body1" sx={{ width: 80 }}>
                    {`Wind`}
                </Typography>
            </Box>
            <Divider />
            {forecastHours
                .filter(({ utcDate }) => {
                    const hour = dayjs(utcDate).hour();

                    return hour % filterBy === 0;
                })
                .map((hour) => {
                    const {
                        utcDate,
                        windDir,
                        windSpeed,
                        temp,
                        RH,
                        pop1Hr,
                        QOP1Hr,
                        tstmPop,
                        dewPoint,
                        fogPop,
                        mslPres,
                        cloudCover,
                        visibility,
                        description,
                        icon,
                    } = hour;
                    const hourKey = dayjs(utcDate).format('HH');

                    return (
                        <Accordion key={hourKey} square elevation={0}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`content-hour-${hourKey}`}
                                id={`hour-${hourKey}`}
                            >
                                <Typography variant="body1" sx={{ width: 80 }}>{`${dayjs(utcDate).format('h a')}`}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: 40 }}>{icon && <WeatherIcon icon={icon} description={description} />}</Box>
                                <Typography variant="body1" sx={{ width: 80 }}>
                                    {numeral(temp).format('0.0')}&#176;
                                </Typography>
                                <Typography variant="body1" sx={{ width: 80 }}>
                                    {`${windDir} ${numeral(windSpeed).format('0')}`}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                <ListItem dense={true} divider>
                                    <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Relative Humidity:`}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {`${numeral(RH).format('0')}%`}
                                    </Typography>
                                </ListItem>
                                <ListItem dense={true} divider>
                                    <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Chance of PCPN:`}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {`${numeral(pop1Hr).format('0')}%`}
                                    </Typography>
                                </ListItem>
                                <ListItem dense={true} divider>
                                    <Typography variant="subtitle1" sx={{ mr: 1 }}>{`PCPN Amount:`}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {`${numeral(QOP1Hr).format('0.00')} IN`}
                                    </Typography>
                                </ListItem>
                                <ListItem dense={true} divider>
                                    <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Chance of TSTM:`}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {`${numeral(tstmPop).format('0')}%`}
                                    </Typography>
                                </ListItem>
                                <ListItem dense={true} divider>
                                    <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Dew Point:`}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {`${numeral(dewPoint).format('0')}`}&#176;
                                    </Typography>
                                </ListItem>
                                <ListItem dense={true} divider>
                                    <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Chance of Fog:`}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {`${numeral(fogPop).format('0')}%`}
                                    </Typography>
                                </ListItem>
                                <ListItem dense={true} divider>
                                    <Typography variant="subtitle1" sx={{ mr: 1 }}>{`MSLP:`}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {`${numeral(mslPres).format('0.0')} MB`}
                                    </Typography>
                                </ListItem>
                                <ListItem dense={true} divider>
                                    <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Sky Cover:`}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {`${numeral(cloudCover).format('0')}%`}
                                    </Typography>
                                </ListItem>
                                <ListItem dense={true} divider>
                                    <Typography variant="subtitle1" sx={{ mr: 1 }}>{`Visibility:`}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {`${numeral(visibility).format('0.0')} MI`}
                                    </Typography>
                                </ListItem>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
        </Box>
    );
};
